import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
    FirebaseMessaging,
    NotificationActionPerformedEvent,
    NotificationReceivedEvent,
    TokenReceivedEvent,
} from '@capacitor-firebase/messaging';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../api/api-base.service';
import { AuthService } from '../../auth/services/auth.service';
import { MobilePlatformService } from './mobile-platform.service';

/**
 * Push nativo via Firebase Cloud Messaging (token FCM unificado iOS/Android).
 * Não usa @capacitor/push-notifications (APNs no iOS não é aceito pelo FCM HTTP v1).
 */
@Injectable({ providedIn: 'root' })
export class NativePushService {
    private readonly apiService = inject(ApiService);
    private readonly authService = inject(AuthService);
    private readonly platform = inject(MobilePlatformService);
    private readonly router = inject(Router);

    private currentToken: string | null = null;
    private listenersReady = false;

    isAvailable(): boolean {
        return (
            this.platform.isNative() &&
            this.platform.isPluginAvailable('FirebaseMessaging')
        );
    }

    async ensureAndroidChannels(): Promise<void> {
        if (!this.platform.isAndroid()) {
            return;
        }
        await FirebaseMessaging.createChannel({
            id: 'infraseg_default',
            name: 'Notificações InfraSeg',
            description: 'Alertas gerais do sistema',
            importance: 4,
            visibility: 1,
            sound: 'default',
            vibration: true,
        });
        await FirebaseMessaging.createChannel({
            id: 'infraseg_panic',
            name: 'Pânico',
            description: 'Alertas críticos de pânico',
            importance: 5,
            visibility: 1,
            sound: 'default',
            vibration: true,
        });
    }

    private attachListeners(): void {
        if (this.listenersReady) {
            return;
        }
        this.listenersReady = true;

        void FirebaseMessaging.addListener('tokenReceived', (event: TokenReceivedEvent) => {
            void this.onRegistration(event.token);
        });

        void FirebaseMessaging.addListener(
            'notificationReceived',
            (event: NotificationReceivedEvent) => {
            console.log('[NativePush] Recebida em foreground:', event.notification);
        });

        void FirebaseMessaging.addListener(
            'notificationActionPerformed',
            (event: NotificationActionPerformedEvent) => {
            const data = event.notification?.data as Record<string, unknown> | undefined;
            this.handleNotificationTap(data);
        });
    }

    private async onRegistration(token: string): Promise<void> {
        if (!token) {
            return;
        }
        this.currentToken = token;

        if (!this.authService.authStateSignal().isAuthenticated) {
            console.log('[NativePush] Token FCM obtido; aguardando login para enviar ao backend');
            return;
        }

        await this.sendTokenToBackend(token);
    }

    async sendTokenToBackend(token: string): Promise<void> {
        try {
            await firstValueFrom(
                this.apiService.post(
                    'notifications/push/register',
                    { token },
                    { showLoader: false, useCache: false },
                ),
            );
            console.log('[NativePush] Token FCM registrado no backend');
        } catch (error) {
            console.error('[NativePush] Falha ao registrar token:', error);
        }
    }

    async checkPermission(): Promise<boolean> {
        if (!this.isAvailable()) {
            return false;
        }
        const status = await FirebaseMessaging.checkPermissions();
        return status.receive === 'granted';
    }

    async requestPermission(): Promise<boolean> {
        if (!this.isAvailable()) {
            return false;
        }
        const status = await FirebaseMessaging.requestPermissions();
        return status.receive === 'granted';
    }

    /**
     * Pede permissão, cria canais (Android) e obtém token FCM.
     */
    async register(): Promise<boolean> {
        if (!this.isAvailable()) {
            return false;
        }

        this.attachListeners();
        await this.ensureAndroidChannels();

        const granted = await this.requestPermission();
        if (!granted) {
            console.warn('[NativePush] Permissão de notificação negada');
            return false;
        }

        try {
            const { token } = await FirebaseMessaging.getToken();
            if (token) {
                await this.onRegistration(token);
            }
        } catch (error) {
            console.error('[NativePush] Erro ao obter token FCM:', error);
            return false;
        }

        return true;
    }

    async unregister(): Promise<void> {
        if (this.currentToken) {
            try {
                await firstValueFrom(
                    this.apiService.post(
                        'notifications/push/unregister',
                        { token: this.currentToken },
                        { showLoader: false, useCache: false },
                    ),
                );
            } catch (error) {
                console.warn('[NativePush] Erro ao remover token no backend:', error);
            }
        }

        try {
            await FirebaseMessaging.deleteToken();
        } catch (error) {
            console.warn('[NativePush] Erro ao apagar token FCM local:', error);
        }

        this.currentToken = null;
    }

    /**
     * Trata o tap em uma notificação.
     * Decisão: sempre navega para a home ('/'), ignorando o payload para evitar
     * rotas inválidas (404) — payload fica apenas como log para debug futuro.
     */
    private handleNotificationTap(data?: Record<string, unknown>): void {
        console.log('[NativePush] Notification tap:', data);
        void this.router.navigateByUrl('/');
    }
}
